import React, {
  useState,
  useCallback,
  ReactNode,
  createContext,
  useContext,
} from "react";
import {
  EDITING_MODAL_LABEL_TOOLTIP,
  EDITING_MODAL_MESSAGE_TOOLTIP,
} from "../constants/Dictionary";
import { PopupConfirm } from "../components/PopupConfirm/PopupConfirm";

type EditingContextType = {
  edit: boolean;
  setEdit: (value: boolean) => void;
  confirmLeave: (action: () => void) => void;
};

const EditingContext = createContext<EditingContextType | undefined>(undefined);

export const EditingProvider = ({ children }: { children: ReactNode }) => {
  const [edit, setEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const confirmLeave = useCallback(
    (action: () => void) => {
      if (edit) {
        setPendingAction(() => action);
        setShowModal(true);
      } else {
        action();
      }
    },
    [edit],
  );

  const handleConfirm = () => {
    setShowModal(false);
    setEdit(false);
    pendingAction?.();
    setPendingAction(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setPendingAction(null);
  };

  return (
    <EditingContext.Provider
      value={{
        edit,
        setEdit,
        confirmLeave,
      }}
    >
      {children}
      {showModal && (
        <PopupConfirm
          label={EDITING_MODAL_LABEL_TOOLTIP}
          popupText={EDITING_MODAL_MESSAGE_TOOLTIP}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          isOpen={showModal}
        />
      )}
    </EditingContext.Provider>
  );
};

export const useEditing = () => {
  const context = useContext(EditingContext);
  if (!context)
    throw new Error("useEditing must be used within EditingProvider");
  return context;
};
